/**
 * useScheduleCategories Hook
 *
 * Fetches schedule categories for filtering using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const ScheduleCategoriesDocument = `
  query ScheduleCategories {
    categories {
      id
      name
      code
      color
      sortOrder
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface ScheduleCategory {
  id: string;
  name: string;
  code: string;
  color: string | null;
  sortOrder: number;
}

interface UseScheduleCategoriesOptions {
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseScheduleCategoriesResult {
  categories: ScheduleCategory[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch schedule categories for filtering.
 *
 * Uses URQL for fetching event categories.
 */
export function useScheduleCategories(
  options: UseScheduleCategoriesOptions = {}
): UseScheduleCategoriesResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    categories: ScheduleCategory[];
  }>({
    query: ScheduleCategoriesDocument,
    pause,
  });

  return {
    categories: result.data?.categories ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
